// SPDX-License-Identifier: GPL-3.0-or-later
//
// User-overridable Typst templates. Drop `cv.typ` / `cover.typ` / `interview.typ`
// under `<root>/.applify/templates/` to override the built-ins. The renderer
// is the same Mustache-shaped engine (see typst/render.ts) — `{{var}}` is
// Typst-escaped, `{{{var}}}` is raw, `{{#section}}…{{/section}}` iterates.
//
// Each kind has up to three files:
//   <kind>.typ          — original (what the user edits, or a built-in copy)
//   <kind>.active.typ   — adjusted version generation actually uses (auto-
//                         written by template-adjust.ts when the profile is
//                         missing info for some placeholders)
//   <kind>.meta.json    — gap list + when the active template was last analyzed

import type { DocKind } from '../generate';
import { coverTemplate } from '../typst/templates/cover';
import { cvTemplate } from '../typst/templates/cv';
import { interviewTemplate } from '../typst/templates/interview';

const APPLIFY_DIR = '.applify';
const TEMPLATES_DIR = 'templates';

const ORIGINAL_FILENAMES: Record<DocKind, string> = {
  cv: 'cv.typ',
  cover: 'cover.typ',
  interview: 'interview.typ',
};

const ACTIVE_FILENAMES: Record<DocKind, string> = {
  cv: 'cv.active.typ',
  cover: 'cover.active.typ',
  interview: 'interview.active.typ',
};

const META_FILENAMES: Record<DocKind, string> = {
  cv: 'cv.meta.json',
  cover: 'cover.meta.json',
  interview: 'interview.meta.json',
};

export const BUILTIN_TEMPLATES: Record<DocKind, string> = {
  cv: cvTemplate,
  cover: coverTemplate,
  interview: interviewTemplate,
};

export interface TemplateGap {
  path: string;
  scope: 'section' | 'field';
  reason: string;
}

export interface TemplateMeta {
  schemaVersion: 1;
  source: 'builtin' | 'user';
  gaps: TemplateGap[];
  analyzedAt: string;
  profileUpdatedAt?: string;
  notes?: string;
  // If the last analysis attempt failed, this holds the message and the
  // .active.typ from the prior successful run (if any) keeps being used.
  error?: string;
}

async function getTemplatesDir(
  root: FileSystemDirectoryHandle,
  create: boolean,
): Promise<FileSystemDirectoryHandle | null> {
  try {
    const applify = await root.getDirectoryHandle(APPLIFY_DIR, { create });
    return await applify.getDirectoryHandle(TEMPLATES_DIR, { create });
  } catch {
    return null;
  }
}

async function readText(
  dir: FileSystemDirectoryHandle,
  name: string,
): Promise<string | null> {
  try {
    const handle = await dir.getFileHandle(name);
    return await (await handle.getFile()).text();
  } catch {
    return null;
  }
}

async function writeText(
  dir: FileSystemDirectoryHandle,
  name: string,
  contents: string,
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  try { await writable.write(contents); }
  finally { await writable.close(); }
}

async function removeFile(
  dir: FileSystemDirectoryHandle,
  name: string,
): Promise<void> {
  try {
    await dir.removeEntry(name);
  } catch {
    // Already gone — nothing to do.
  }
}

// Public: what generation reads. Prefer the AI-adjusted .active.typ; fall
// back to the user-edited .typ; null means "use the built-in".
export async function loadCustomTemplate(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
): Promise<string | null> {
  const dir = await getTemplatesDir(root, false);
  if (!dir) return null;
  return (
    (await readText(dir, ACTIVE_FILENAMES[kind])) ??
    (await readText(dir, ORIGINAL_FILENAMES[kind]))
  );
}

export async function readOriginalTemplate(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
): Promise<string | null> {
  const dir = await getTemplatesDir(root, false);
  if (!dir) return null;
  return readText(dir, ORIGINAL_FILENAMES[kind]);
}

export async function readActiveTemplate(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
): Promise<string | null> {
  const dir = await getTemplatesDir(root, false);
  if (!dir) return null;
  return readText(dir, ACTIVE_FILENAMES[kind]);
}

export async function writeActiveTemplate(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
  contents: string,
): Promise<void> {
  const dir = await getTemplatesDir(root, true);
  if (!dir) throw new Error('Could not create .applify/templates/');
  await writeText(dir, ACTIVE_FILENAMES[kind], contents);
}

// Overwrite the user-facing original (<kind>.typ). Used by the CV-import
// flow when the AI synthesises a new template from an uploaded file.
export async function writeOriginalTemplate(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
  contents: string,
): Promise<void> {
  const dir = await getTemplatesDir(root, true);
  if (!dir) throw new Error('Could not create .applify/templates/');
  await writeText(dir, ORIGINAL_FILENAMES[kind], contents);
}

export async function clearActiveTemplate(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
): Promise<void> {
  const dir = await getTemplatesDir(root, false);
  if (!dir) return;
  await removeFile(dir, ACTIVE_FILENAMES[kind]);
}

// Ensure `<kind>.typ` exists on disk. If missing, seed from the built-in and
// return { source: 'builtin' }. If already present, return { source: 'user' }
// (the user either dropped one or we wrote one earlier). The returned text is
// the original we should analyze against.
export async function ensureOriginalTemplate(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
): Promise<{ source: 'builtin' | 'user'; text: string }> {
  const dir = await getTemplatesDir(root, true);
  if (!dir) throw new Error('Could not create .applify/templates/');
  const existing = await readText(dir, ORIGINAL_FILENAMES[kind]);
  if (existing !== null) return { source: 'user', text: existing };
  const builtin = BUILTIN_TEMPLATES[kind];
  await writeText(dir, ORIGINAL_FILENAMES[kind], builtin);
  return { source: 'builtin', text: builtin };
}

export async function readTemplateMeta(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
): Promise<TemplateMeta | null> {
  const dir = await getTemplatesDir(root, false);
  if (!dir) return null;
  const text = await readText(dir, META_FILENAMES[kind]);
  if (!text || !text.trim()) return null;
  try {
    return JSON.parse(text) as TemplateMeta;
  } catch {
    return null;
  }
}

export async function writeTemplateMeta(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
  meta: TemplateMeta,
): Promise<void> {
  const dir = await getTemplatesDir(root, true);
  if (!dir) throw new Error('Could not create .applify/templates/');
  await writeText(dir, META_FILENAMES[kind], JSON.stringify(meta, null, 2));
}

// Whether the user (or a prior analysis) has written an original template to
// disk for each kind. Used by Settings to show "using your template" vs the
// built-in dot indicator.
export async function listCustomTemplates(
  root: FileSystemDirectoryHandle,
): Promise<Record<DocKind, boolean>> {
  return {
    cv: (await readOriginalTemplate(root, 'cv')) !== null,
    cover: (await readOriginalTemplate(root, 'cover')) !== null,
    interview: (await readOriginalTemplate(root, 'interview')) !== null,
  };
}

export async function writeDefaultTemplates(
  root: FileSystemDirectoryHandle,
): Promise<{ written: DocKind[] }> {
  const dir = await getTemplatesDir(root, true);
  if (!dir) throw new Error('Could not create .applify/templates/');
  const written: DocKind[] = [];
  for (const kind of ['cv', 'cover', 'interview'] as DocKind[]) {
    // Don't overwrite an existing user template.
    try {
      await dir.getFileHandle(ORIGINAL_FILENAMES[kind]);
      continue; // Already exists.
    } catch { /* fall through */ }
    await writeText(dir, ORIGINAL_FILENAMES[kind], BUILTIN_TEMPLATES[kind]);
    written.push(kind);
  }
  return { written };
}
